function normalizeBaseUrl(url: string): string {
  return url.replace(/\/+$/, '');
}

type ApiBaseLocation = Pick<Location, 'protocol' | 'hostname' | 'port' | 'origin'>;

export function isLocalHostname(hostname: string): boolean {
  return hostname === 'localhost' || hostname === '127.0.0.1' || hostname === '::1';
}

export function isProductionApiUrl(url: string): boolean {
  try {
    return new URL(url).hostname === 'api.omniscribe.ai';
  } catch {
    return false;
  }
}

export function shouldUseLocalApiOverride(
  configuredUrl: string | undefined,
  location: ApiBaseLocation | undefined,
  isDev: boolean,
): boolean {
  const envUrl = configuredUrl?.trim();
  return Boolean(isDev && envUrl && location && isLocalHostname(location.hostname) && isProductionApiUrl(envUrl));
}

export function resolveApiBaseUrlFromLocation(
  configuredUrl: string | undefined,
  location: ApiBaseLocation | undefined,
  isDev: boolean,
): string {
  const envUrl = configuredUrl?.trim();
  if (envUrl) {
    if (location && shouldUseLocalApiOverride(envUrl, location, isDev)) {
      return `${location.protocol}//${location.hostname}:8081/api/v1`;
    }

    return normalizeBaseUrl(envUrl);
  }

  if (location) {
    const { protocol, hostname, port, origin } = location;

    // Vite dev server defaults to :3000 in this repo. When opened from a phone on LAN,
    // we want API calls to hit the same host on backend port 8081.
    if (port === '3000') {
      return `${protocol}//${hostname}:8081/api/v1`;
    }

    // Production deployments often serve API on the same origin.
    return `${origin}/api/v1`;
  }

  // Fallback for non-browser contexts
  return 'http://localhost:8081/api/v1';
}

export function resolveApiBaseUrl(): string {
  return resolveApiBaseUrlFromLocation(
    import.meta.env.VITE_API_URL as string | undefined,
    typeof window !== 'undefined' ? window.location : undefined,
    import.meta.env.DEV,
  );
}
